import React, { Suspense, lazy } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { SEO } from '@/components/SEO';

// The three dashboards are separate files loaded when the signed-in user needs them,
// so a first visit (home, catalogue, listing pages) does not download them.
//
// If a file cannot be fetched (a new version was published while the page was open, a file
// was removed after a deployment, or the connection dropped):
//   1. online: the page reloads ONCE to pick up the new files (never in a loop);
//   2. otherwise, or if it fails again: a message with a "Try again" button is shown
//      (DashboardLoadBoundary) instead of an empty page. The first click tries the file
//      again in place; if that fails too, the next click reloads the page.
const RELOAD_FLAG = 'ridalo_dashboard_reload';
const LOADERS = {
  client: () => import('@/pages/ClientDashboard').then((m) => ({ default: m.ClientDashboard })),
  provider: () => import('@/pages/ProviderDashboard').then((m) => ({ default: m.ProviderDashboard })),
  admin: () => import('@/pages/AdminDashboard').then((m) => ({ default: m.AdminDashboard })),
};

const lazyDashboard = (load) => lazy(() => load().then(
  (mod) => {
    try { sessionStorage.removeItem(RELOAD_FLAG); } catch { /* storage unavailable */ }
    return mod;
  },
  (error) => {
    const failure = error && typeof error === 'object' ? error : new Error(String(error));
    failure.dashboardLoadFailure = true;
    let canReload = typeof navigator === 'undefined' || navigator.onLine !== false;
    try {
      if (sessionStorage.getItem(RELOAD_FLAG) === '1') canReload = false;
      else if (canReload) sessionStorage.setItem(RELOAD_FLAG, '1');
    } catch { canReload = false; }          // storage unavailable: no reload loop is possible
    if (!canReload) throw failure;
    window.location.reload();
    return new Promise(() => {});           // the page is reloading: keep showing the loader
  },
));

// Current lazy components; recreated on "Try again" (a failed lazy component keeps its failure).
const dashboards = {};
const createDashboards = () => { Object.keys(LOADERS).forEach((k) => { dashboards[k] = lazyDashboard(LOADERS[k]); }); };
createDashboards();

// Reads the current lazy component at render time, so "Try again" picks up the recreated one.
const Dashboard = ({ name }) => { const Page = dashboards[name]; return <Page />; };

class DashboardLoadBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, attempt: 0, retriedInPlace: false };
    this.retry = this.retry.bind(this);
  }

  static getDerivedStateFromError(error) { return { error }; }

  retry() {
    if (this.state.retriedInPlace) { window.location.reload(); return; }
    createDashboards();
    this.setState((s) => ({ error: null, attempt: s.attempt + 1, retriedInPlace: true }));
  }

  render() {
    const { error, attempt } = this.state;
    // Anything that is not a failed download keeps its previous behaviour.
    if (error && !error.dashboardLoadFailure) throw error;
    if (error) return <DashboardLoadFailed onRetry={this.retry} />;
    return <React.Fragment key={attempt}>{this.props.children}</React.Fragment>;
  }
}

const DashboardLoadFailed = ({ onRetry }) => {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen flex items-center justify-center px-4" role="alert" data-testid="dashboard-load-failed">
      <div className="max-w-sm text-center space-y-4">
        <h1 className="text-xl font-semibold">{t('dashboardLoad.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('dashboardLoad.body')}</p>
        <button type="button" onClick={onRetry} data-testid="dashboard-load-retry"
          className="px-4 py-2 rounded-md bg-accent text-white">{t('dashboardLoad.retry')}</button>
      </div>
    </div>
  );
};

// Same spinner as the session check (ProtectedRoute): one continuous loading look.
const DashboardLoading = () => (
  <div className="min-h-screen flex items-center justify-center" role="status" aria-busy="true" data-testid="dashboard-loading">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent"></div>
  </div>
);

export const DashboardRouter = () => {
  const { user } = useAuth();
  const { t } = useTranslation();

  if (!user) return null;

  let page;
  switch (user.role) {
    case 'client':
      page = <Dashboard name="client" />;
      break;
    case 'provider':
      page = <Dashboard name="provider" />;
      break;
    case 'admin':
      // Administration is intentionally left untouched.
      return <DashboardLoadBoundary><Suspense fallback={<DashboardLoading />}><Dashboard name="admin" /></Suspense></DashboardLoadBoundary>;
    default:
      page = <Dashboard name="client" />;
  }
  // Private area: never indexed.
  return (
    <>
      <SEO title={t('seo.dashboard.title')} noindex />
      <DashboardLoadBoundary><Suspense fallback={<DashboardLoading />}>{page}</Suspense></DashboardLoadBoundary>
    </>
  );
};